import { t } from '@lingui/core/macro';
import type { AddressFallbackRecord } from '@navio/nsdk';
import { AddressLabel } from '@navio/nsdk';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { cn } from 'noddi-ui-common';
import { FieldErrorMessage, NavioOverlayActions, NoddiButton, type NoddiButtonProps } from 'noddi-ui-web';
import { CountryCode } from 'noddi-util';
import { AddressPicker, getLongAddressName } from 'noddi-web';
import { useState } from 'react';
import { buildManualProfileAddressBody, isAddressFallbackComplete } from '../../utils/addressFallback';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { SavedAddressList } from '../Address/SavedAddressList';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';

type Props = {
  userGroupId: number;
  /**
   * Label the saved link carries. The data-quality report counts an invoice
   * address only when the link is labelled `invoice`, so that step passes it.
   */
  label?: AddressLabel;
  /** ISO alpha-2 for manual create; defaults to NO when unknown. */
  countryCode?: string;
  ctaLabel: string;
  ctaVariant?: NoddiButtonProps['variant'];
  ctaClassName?: string;
  /** Vertical rhythm between picker, errors and CTA — roomier inside a phone sheet. */
  gapClassName?: string;
  /** Inside a dialog the global toast is gone before it can be read — see mutations skill. */
  showApiErrorInline?: boolean;
  /** In-flow search instead of a popover — set inside a bottom sheet, where the keyboard covers a popover. */
  inlineSearch?: boolean;
  onSaved: () => void;
};

/**
 * Places search or manual entry → saved as a user-group address.
 * The manual path uses atomic create-and-attach (nested address body).
 *
 * An address the group already holds is relabelled rather than created again:
 * (user_group, address) is unique, so a second create is rejected, and one link
 * carries one label.
 */
export const ProfileAddressForm = ({
  userGroupId,
  label = AddressLabel.HOME,
  countryCode = CountryCode.NORWAY,
  ctaLabel,
  ctaVariant,
  ctaClassName,
  gapClassName,
  showApiErrorInline = false,
  inlineSearch = false,
  onSaved
}: Props) => {
  const [selectedAddress, setSelectedAddress] = useState<{ id: number; name: string } | undefined>();
  const [addressFallback, setAddressFallback] = useState<AddressFallbackRecord | null>(null);
  const [showAddressError, setShowAddressError] = useState(false);
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);

  // One page covers any real customer; the list also carries addresses of groups
  // this one can see, so the group's own links are picked out below.
  const { data: userGroupAddresses } = noddiAsync.useGet({
    type: URLKeys.getUserGroupAddresses,
    input: { userGroupId, pageSize: 100 }
  });

  const onAddressSaved = async () => {
    await invalidateQuery({ urlKey: URLKeys.getUserGroupAddresses });
    await invalidateQuery({ urlKey: URLKeys.getBookingInfo });
    setSelectedAddress(undefined);
    setAddressFallback(null);
    onSaved();
  };

  const {
    mutate: createUserGroupAddress,
    isPending: isCreating,
    error: createAddressError
  } = noddiAsync.usePost({
    type: URLKeys.postUserGroupAddress,
    queryConfig: { onSuccess: onAddressSaved }
  });

  const {
    mutate: relabelUserGroupAddress,
    isPending: isRelabelling,
    error: relabelAddressError
  } = noddiAsync.usePatch({
    type: URLKeys.patchUserGroupAddress,
    queryConfig: { onSuccess: onAddressSaved }
  });

  const isSaving = isCreating || isRelabelling;
  const saveError = createAddressError ?? relabelAddressError;
  const savedLinks = (userGroupAddresses?.results ?? []).filter((item) => item.userGroupId === userGroupId);
  const savedAddressNames = savedLinks.map((item) => getLongAddressName(item.address));

  const handleSave = () => {
    if (selectedAddress) {
      const existingLink = userGroupAddresses?.results.find(
        (item) => item.address.id === selectedAddress.id && item.userGroupId === userGroupId
      );

      if (existingLink) {
        if (existingLink.label === label) {
          onSaved();
          return;
        }
        relabelUserGroupAddress({ userGroupAddressId: existingLink.id, label });
        return;
      }

      createUserGroupAddress({
        userGroupId,
        addressId: selectedAddress.id,
        label
      });
      return;
    }

    if (!addressFallback || !isAddressFallbackComplete(addressFallback)) {
      setShowAddressError(true);
      return;
    }

    // Atomic: BE get-or-creates Address and attaches UserGroupAddress in one transaction.
    createUserGroupAddress({
      userGroupId,
      label,
      address: buildManualProfileAddressBody(addressFallback, countryCode)
    });
  };

  return (
    <div className={cn('flex flex-col gap-4', gapClassName)}>
      {!selectedAddress && (
        <SavedAddressList
          items={savedLinks.map((item) => {
            const title = item.name || item.customLabel;
            return {
              id: item.address.id,
              title: title || undefined,
              addressName: getLongAddressName(item.address)
            };
          })}
          onSelect={(id) => {
            const link = savedLinks.find((item) => item.address.id === id);
            if (!link) {
              return;
            }
            setSelectedAddress({ id: link.address.id, name: getLongAddressName(link.address) });
            setAddressFallback(null);
            setShowAddressError(false);
          }}
        />
      )}
      <AddressPicker
        hideLabel
        enableFallback
        inlineSearch={inlineSearch}
        skipIsInDeliveryAreaCheck
        address={selectedAddress}
        onAddressResolutionChange={setIsResolvingAddress}
        onAddressCreation={(address) => {
          setSelectedAddress(address);
          setShowAddressError(false);
        }}
        onFallbackChange={(fallback) => {
          setAddressFallback(fallback);
          setShowAddressError(false);
        }}
        savedAddressNames={savedAddressNames}
        translations={getCommonTranslations().addressSearch}
        fallbackTranslations={{
          cantFindAddress: t`Can't find your address? Enter it manually`,
          backToSearch: t`Back to address search`,
          address: t`Address`,
          addressPlaceholder: t`Address`,
          streetNumber: t`Number`,
          streetNumberPlaceholder: t`Nr.`,
          zipcode: t`Zipcode`,
          zipcodePlaceholder: t`Zipcode`,
          city: t`City`,
          cityPlaceholder: t`City`
        }}
      />
      {showAddressError && (
        <FieldErrorMessage errorMessage={t`Select an address from the list or complete all manual address fields`} />
      )}
      {showApiErrorInline && saveError && <ApiErrorMessageWithTrans error={saveError} />}

      {/* `justify-start` keeps the page card's `sm:w-fit` CTA left-aligned; in the data-quality dialog it is full width. */}
      <NavioOverlayActions className='justify-start'>
        <NoddiButton
          variant={ctaVariant}
          className={ctaClassName}
          loading={isSaving || isResolvingAddress}
          disabled={isSaving || isResolvingAddress}
          onClick={handleSave}
        >
          {ctaLabel}
        </NoddiButton>
      </NavioOverlayActions>
    </div>
  );
};
